import { useState, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { matchChannelNames, bulkAssignPlansByChannelIds, type ChannelMatchResult } from '@/services/channels/matching';
import { toast } from 'sonner';

export interface UseBulkPlanAssignerOptions {
  providerId: number;
  versionId: number;
  regionId?: number | null;
  onSuccess?: (assignedCount: number) => void;
}

export function useBulkPlanAssigner({
  providerId,
  versionId,
  regionId,
  onSuccess
}: UseBulkPlanAssignerOptions) {
  const [matchResult, setMatchResult] = useState<ChannelMatchResult | null>(null);
  const [isMatching, setIsMatching] = useState(false);
  const queryClient = useQueryClient();

  // Channel matching mutation
  const matchChannelsMutation = useMutation({
    mutationFn: async (channelNames: string) => {
      return matchChannelNames(channelNames, providerId, versionId, regionId);
    },
    onSuccess: (result) => {
      setMatchResult(result);
      
      // Show summary toast
      const { totalInput, totalFound, exact, fuzzy } = result;
      if (totalFound > 0) {
        toast.success(`Found ${totalFound} of ${totalInput} channels (${exact.length} exact, ${fuzzy.length} fuzzy matches)`);
      } else if (totalInput > 0) {
        toast.error(`Could not match any of the ${totalInput} channel names provided.`);
      }
    },
    onError: (error) => {
      console.error('Channel matching error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to match channel names. Please try again.');
      setMatchResult(null);
    }
  });

  // Plan assignment mutation
  const assignPlansMutation = useMutation({
    mutationFn: async ({ channelIds, planId }: { channelIds: number[], planId: number }) => {
      return bulkAssignPlansByChannelIds(channelIds, planId, versionId);
    },
    onSuccess: (_, { channelIds }) => {
      // Invalidate relevant queries to refresh the UI
      queryClient.invalidateQueries({ queryKey: ['channels', providerId, versionId] });
      queryClient.invalidateQueries({ queryKey: ['channels', providerId, versionId, regionId] });

      toast.success(`Assigned plan to ${channelIds.length} channels.`);

      onSuccess?.(channelIds.length);
      setMatchResult(null); // Clear match result after successful assignment
    },
    onError: (error) => {
      console.error('Plan assignment error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to assign plans to channels.');
    }
  });

  // Match channels with debouncing and validation
  const matchChannels = useCallback(async (channelNames: string) => {
    if (!channelNames.trim()) {
      setMatchResult(null);
      return;
    }

    setIsMatching(true);
    try {
      await matchChannelsMutation.mutateAsync(channelNames);
    } finally {
      setIsMatching(false);
    }
  }, [matchChannelsMutation]);

  // Assign plans to matched channels
  const assignPlans = useCallback(async (planId: number) => {
    if (!matchResult || matchResult.totalFound === 0) {
      toast.error('Please match channels first before assigning plans.');
      return;
    }

    // Collect all matched channel IDs
    const channelIds = [
      ...matchResult.exact.map(m => m.id),
      ...matchResult.fuzzy.map(m => m.id)
    ];

    if (channelIds.length === 0) {
      toast.error('No valid channels found to assign plans to.');
      return;
    }

    await assignPlansMutation.mutateAsync({ channelIds, planId });
  }, [matchResult, assignPlansMutation]);

  // Reset state
  const reset = useCallback(() => {
    setMatchResult(null);
    setIsMatching(false);
  }, []);

  return {
    // State
    matchResult,
    isMatching,
    isAssigning: assignPlansMutation.isPending,
    
    // Actions
    matchChannels,
    assignPlans,
    reset,
    
    // Computed values
    hasMatches: matchResult && matchResult.totalFound > 0,
    hasExactMatches: matchResult && matchResult.exact.length > 0,
    hasFuzzyMatches: matchResult && matchResult.fuzzy.length > 0,
    hasNotFoundChannels: matchResult && matchResult.notFound.length > 0,
    
    // Error states
    matchError: matchChannelsMutation.error,
    assignError: assignPlansMutation.error
  };
}